import { motion } from "framer-motion";
import { Award, GraduationCap, Briefcase, Linkedin, ArrowUpRight } from "lucide-react";
import { FOUNDER, SITE } from "@/lib/site";

export default function Founder() {
  return (
    <section
      data-testid="founder-section"
      id="founder"
      className="section relative bg-gradient-to-b from-[#080808] via-[#050505] to-[#080808]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-[#D4AF37]/30 to-transparent blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30">
              <img
                src={FOUNDER.photo}
                alt={`${FOUNDER.name} — Founder, Axomind Asset Services LLP`}
                className="w-full h-[560px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass-strong rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-white">{FOUNDER.name}</div>
                  <div className="text-xs text-[#D4AF37] tracking-wide">Founder & Principal</div>
                </div>
                <a
                  href={SITE.socials.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${FOUNDER.name} on LinkedIn`}
                  data-testid="founder-linkedin"
                  className="w-10 h-10 rounded-full bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#FFD700] transition relative z-10"
                >
                  <Linkedin size={16} />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Meet The Founder</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05] tracking-tight">
            {FOUNDER.name.split(" ")[0]}{" "}
            <span className="text-gold-gradient">{FOUNDER.name.split(" ").slice(1).join(" ")}</span>
          </h2>
          <p className="mt-3 text-gray-400 text-base">{FOUNDER.title}</p>

          <p className="mt-6 text-gray-300 text-base lg:text-lg leading-relaxed">{FOUNDER.short}</p>

          <div className="mt-6 space-y-4">
            {FOUNDER.bio.map((p, i) => (
              <p key={i} className="text-gray-400 leading-relaxed">{p}</p>
            ))}
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {[
              { icon: <GraduationCap size={18} />, text: "MBA (Finance), Symbiosis" },
              { icon: <Award size={18} />, text: "CFA Institute IFP (USA)" },
              { icon: <Briefcase size={18} />, text: "TATA AMC • SBI MF • Kotak AMC" },
            ].map((c, i) => (
              <div
                key={i}
                className="glass rounded-xl p-4 flex items-start gap-3"
                data-testid={`founder-credential-${i}`}
              >
                <span className="text-[#D4AF37] mt-0.5">{c.icon}</span>
                <span className="text-sm text-gray-200 leading-snug">{c.text}</span>
              </div>
            ))}
          </div>

          <a
            href={SITE.socials.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="founder-linkedin-cta"
            aria-label={`Connect with ${FOUNDER.name} on LinkedIn`}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A66C2] text-white font-semibold hover:bg-[#0E76D4] transition shadow-[0_10px_30px_-10px_rgba(10,102,194,0.5)] group"
          >
            <Linkedin size={18} fill="currentColor" />
            Connect on LinkedIn
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
